import React, { useEffect, useId, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { getModelCatalog } from '../../services/modelCatalog';
import { getCapabilities, getModelSlot, getDetectionState, getAudienceValidation } from '../../utils/models/capabilities';
import utils from '../../utils';
import modelUtils from '../../utils/models';
import { audienceModelSections, getAudienceModelGroup } from '../../utils/models/audience';
import { buildModelCatalog, classifierTypes, filterModelCatalog, modelPlatforms, updateAudienceModels } from '../../utils/models/catalog';
import AddModel from './addModel';
import UnsupportedModelIcon from './unsupportedModelIcon';
import './audienceModels.scss';

const EMPTY_DRIVER = {};
const AudienceModels = ({ driverConfig = EMPTY_DRIVER, onChange, isEditable }) => {
  const id = useId();
  const [catalog, setCatalog] = useState([]);
  const [catalogStatus, setCatalogStatus] = useState('Loading model catalog…');
  const [customError, setCustomError] = useState('');
  useEffect(() => {
    let cancelled = false;
    getModelCatalog().then(response => {
      if (!cancelled) { setCatalog(buildModelCatalog(response)); setCatalogStatus(''); }
    }).catch(() => { if (!cancelled) setCatalogStatus('Model catalog unavailable. Custom versions are still available.'); });
    return () => { cancelled = true; };
  }, []);
  const [platform, setPlatform] = useState('');
  const [picker, setPicker] = useState(null);
  const [custom, setCustom] = useState(false);
  const [version, setVersion] = useState('');
  const [type, setType] = useState('');
  const [advanced, setAdvanced] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const models = driverConfig.models || [];
  const detection = getDetectionState(driverConfig);
  const validation = getAudienceValidation(driverConfig);

  useEffect(() => {
    // Saving, cancelling or changing the source config invalidates any modal draft.
    setPicker(null);
    setAdvanced(null);
    setAddOpen(false);
  }, [driverConfig, isEditable]);

  const commit = (nextModels, nextMode, selectedType) => {
    if (isEditable) onChange(updateAudienceModels(driverConfig, nextModels, nextMode, selectedType));
  };
  const openPicker = (group, index = null) => {
    const current = index !== null ? models[index] : null;
    setPicker({ group, index });
    setCustom(false);
    setCustomError('');
    setVersion(current ? current.version || '' : '');
    setType(current ? current.type : classifierTypes[group] || ({ face: 'SSD-DETECTOR', alignment: 'ALIGN', person: 'YOLOX' })[group] || '');
  };
  const applyModel = model => {
    if (!isEditable) return;
    if (getModelSlot(model) === 'face' && model.config && 'is_face_person_model' in model.config) {
      model = { ...model, config: { ...model.config, is_face_person_model: getCapabilities(model).includes('person') } };
    }
    let next = [...models];
    if (['face', 'alignment', 'person'].includes(picker.group)) {
      const first = next.findIndex(item => getModelSlot(item) === picker.group);
      next = next.filter(item => getModelSlot(item) !== picker.group);
      next.splice(first < 0 ? next.length : first, 0, model);
    } else if (picker.index !== null) next[picker.index] = model;
    else next.push(model);
    commit(next, undefined, model.type);
    setPicker(null);
  };
  const applyCustom = event => {
    event.preventDefault();
    if (!version.trim() || !type.trim()) return;
    const current = picker.index !== null ? models[picker.index] : {};
    const model = { ...utils.deepClone(current), type: type.trim().toUpperCase(), version: version.trim() };
    if (['face', 'alignment', 'person'].includes(picker.group) && getModelSlot(model) !== picker.group) {
      setCustomError('This model type does not provide the selected capability.');
      return;
    }
    applyModel(model);
  };
  const openAdvanced = index => setAdvanced({ index, model: utils.deepClone(models[index]) });
  const changeAdvanced = (path, value) => {
    const model = utils.deepClone(advanced.model);
    utils.set(model, path, value);
    setAdvanced({ ...advanced, model });
  };
  const currentModel = picker && picker.index !== null ? models[picker.index] : null;
  const choices = picker ? filterModelCatalog(catalog, platform).filter(entry => {
    if (['face', 'alignment', 'person'].includes(picker.group)) return getModelSlot(entry.model) === picker.group;
    if (picker.group === 'age') return entry.capabilities.includes('age') && !entry.capabilities.includes('mtl');
    return currentModel ? entry.model.type === currentModel.type : entry.capabilities.includes(picker.group) && (picker.group === 'mtl' || !entry.capabilities.includes('mtl'));
  }) : [];
  const renderRow = (model, index, label) => (
    <div className="audience-model-row" key={index}>
      <div className="audience-model-label-group">
        <UnsupportedModelIcon model={model} />
        <label className="audience-model-name">
        <input type="checkbox" checked={model.type !== 'GENDER_FULL' || (driverConfig.config || {}).full_body_gender !== false} disabled={!isEditable}
          onChange={event => {
            if (model.type === 'GENDER_FULL' && event.target.checked) {
              commit(models, undefined, 'GENDER_FULL');
            } else commit(models.filter((_, i) => i !== index));
          }} />
        <span>{label}</span>
        </label>
      </div>
      <div className="audience-model-actions">
        <button type="button" className="btn audience-version-button" disabled={!isEditable}
          aria-label={`Choose ${label} version`} onClick={() => openPicker(getAudienceModelGroup(model), index)}>
          <span>{model.version ? `${model.type}:${model.version}` : 'Choose version'}</span><i className="uil uil-angle-down" aria-hidden="true" />
        </button>
        <button type="button" className="btn audience-settings-button" aria-label={`Advanced ${label} settings`}
          disabled={model.type === 'GENDER_FULL' && (driverConfig.config || {}).full_body_gender === false}
          onClick={() => openAdvanced(index)}><i className="uil uil-sliders-v-alt" aria-hidden="true" /></button>
      </div>
    </div>
  );
  const labels = { face: 'Face detection', alignment: 'Alignment', person: 'Person detection' };
  const renderSlot = slot => {
    const model = detection[slot];
    const included = slot === 'alignment' ? detection.alignmentIncluded : slot === 'person' && detection.personIncluded;
    const label = labels[slot];
    return (
      <div className="audience-model-row" key={slot}>
        <div className="audience-slot-name">
          {slot === 'person' && !included ? <label className="audience-model-name">
            <input type="checkbox" checked={!!model} disabled={!isEditable} onChange={() => model ? commit(models.filter(item => getModelSlot(item) !== 'person')) : openPicker(slot)} />
            <span>{label}</span>
          </label> : <span>{label}</span>}
          {slot === 'face' && model && <small>{getCapabilities(model).filter(cap => ['face', 'person', 'alignment'].includes(cap)).join(' · ')}</small>}
          {slot === 'alignment' && !included && <small className={!model ? 'text-danger' : ''}>Required</small>}
          {slot === 'person' && !included && <small>{(driverConfig.config || {}).full_body_gender ? 'Required for Full Body Gender' : 'Optional'}</small>}
        </div>
        <div className="audience-model-actions">
          {included ? <span className="audience-included">✓ Included in {detection.face.type}</span> :
            <button type="button" className="btn audience-version-button" disabled={!isEditable} aria-label={`Choose ${label} version`}
              onClick={() => openPicker(slot, model ? models.indexOf(model) : null)}>
              <span>{model ? `${model.type}:${model.version}` : 'Choose version'}</span><i className="uil uil-angle-down" aria-hidden="true" />
            </button>}
          <button type="button" className="btn audience-settings-button" disabled={included || !model} aria-label={`Advanced ${label} settings`}
            onClick={() => openAdvanced(models.indexOf(model))}><i className="uil uil-sliders-v-alt" aria-hidden="true" /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="audience-models">
      <label className="audience-platform" htmlFor={`${id}-platform`}>
        <span>Device platform</span>
        <select id={`${id}-platform`} className="form-control" value={platform} onChange={e => setPlatform(e.target.value)}>
          <option value="">All platforms</option>{modelPlatforms.map(item => <option key={item}>{item}</option>)}
        </select>
      </label>
      <div className="section-title audience-model-section-title"><h4>Detection Models</h4></div>
      {['face', 'alignment', 'person'].map(renderSlot)}
      {!!validation.length && <div className="audience-model-validation" role="status">{validation.map(error => <div key={error}>{error}</div>)}</div>}
      <div className="section-title audience-model-section-title"><h4>Classification Models</h4></div>
      {audienceModelSections[1].groups.map(group => {
        const entries = models.map((model, index) => ({ model, index })).filter(({ model }) => getAudienceModelGroup(model) === group.id);
        return entries.length ? entries.map(({ model, index }) => renderRow(model, index, group.label)) : (
          <div className="audience-model-row" key={group.id}>
            <label className="audience-model-name"><input type="checkbox" checked={false} disabled={!isEditable} onChange={() => openPicker(group.id)} /><span>{group.label}</span></label>
            <div className="audience-model-actions">
              <button type="button" className="btn audience-version-button" disabled={!isEditable} onClick={() => openPicker(group.id)}
                aria-label={`Choose ${group.label} version`}><span>Choose version</span> <i className="uil uil-angle-down" aria-hidden="true" /></button>
              <button type="button" className="btn audience-settings-button" disabled aria-label={`Advanced ${group.label} settings`}>
                <i className="uil uil-sliders-v-alt" aria-hidden="true" />
              </button>
            </div>
          </div>
        );
      })}
      {models.some(model => getAudienceModelGroup(model) === 'other') && <div className="section-title audience-model-section-title"><h4>Supporting Models</h4></div>}
      {models.map((model, index) => getAudienceModelGroup(model) === 'other' ? renderRow(model, index, model.type) : null)}
      <button type="button" className="btn btn-link audience-add-model" disabled={!isEditable} onClick={() => setAddOpen(true)}>+ Add another model</button>
      <AddModel isOpenModal={addOpen} onClose={() => setAddOpen(false)} onAddModel={model => { commit([...models, model], undefined, model.type); setAddOpen(false); }} />

      <Modal isOpen={!!picker} toggle={() => setPicker(null)} fade={false} centered size="lg" className="audience-model-modal audience-catalog-modal">
        <ModalHeader toggle={() => setPicker(null)} close={<button type="button" className="close" aria-label="Close model picker" onClick={() => setPicker(null)}><span aria-hidden="true">×</span></button>}>Choose {picker && (labels[picker.group] || (audienceModelSections.flatMap(section => section.groups).find(group => group.id === picker.group) || { label: type }).label)} model</ModalHeader>
        <ModalBody>
          <label className="audience-platform" htmlFor={`${id}-picker-platform`}>Device platform
            <select id={`${id}-picker-platform`} className="form-control" value={platform} onChange={e => setPlatform(e.target.value)}>
              <option value="">All platforms</option>{modelPlatforms.map(item => <option key={item}>{item}</option>)}
            </select>
          </label>
          {currentModel && <p className="audience-current-model">Current: {currentModel.type}:{currentModel.version}</p>}
          <div className="audience-model-catalog">
            {choices.map((entry, index) => (
              <button type="button" className="audience-catalog-option" key={index} disabled={!isEditable} onClick={() => applyModel(utils.deepClone(entry.model))}>
                <div className="audience-catalog-heading"><strong>{entry.model.type}:{entry.model.version}</strong>{entry.recommended && <span className="audience-recommended">RECOMMENDED</span>}</div>
                <span>{entry.capabilities.join(' · ')}</span>
                {entry.description && <span>{entry.description}</span>}
                <small>{entry.platforms.join(', ')}{entry.platformSupportStatus === 'provisional' && ' · Provisional support'}</small>
              </button>
            ))}
            {catalogStatus && <p role="status">{catalogStatus}</p>}
            {!catalogStatus && !choices.length && <p>No catalog versions match this selection. Choose another platform or enter a custom version.</p>}
          </div>
          <button type="button" className="btn btn-link px-0" aria-expanded={custom} aria-controls={`${id}-custom`} disabled={!isEditable} onClick={() => setCustom(!custom)}>+ Use a custom version</button>
          {custom && <form id={`${id}-custom`} onSubmit={applyCustom}>
            {picker && ['face', 'alignment', 'person', 'age'].includes(picker.group) && <label className="audience-model-field">Model type<input className="form-control" value={type} required onChange={e => setType(e.target.value)} /></label>}
            <label className="audience-model-field">Version<input className="form-control" value={version} required autoFocus onChange={e => setVersion(e.target.value)} /></label>
            {customError && <p role="alert" className="text-danger">{customError}</p>}
            <small>Custom versions are not checked for availability or hardware compatibility.</small>
            <button className="btn btn-primary mt-3" type="submit" disabled={!isEditable || !version.trim() || !type.trim()}>Use version</button>
          </form>}
        </ModalBody>
        <ModalFooter><button className="btn btn-secondary" type="button" onClick={() => setPicker(null)}>Cancel</button></ModalFooter>
      </Modal>
      <Modal isOpen={!!advanced} toggle={() => setAdvanced(null)} fade={false} centered className="audience-model-modal">
        <ModalHeader toggle={() => setAdvanced(null)} close={<button type="button" className="close" aria-label="Close advanced settings" onClick={() => setAdvanced(null)}><span aria-hidden="true">×</span></button>}>{advanced && advanced.model.type} settings</ModalHeader>
        {advanced && <form onSubmit={e => {
          e.preventDefault();
          commit(models.map((model, index) => index === advanced.index ? advanced.model : model));
          setAdvanced(null);
        }}>
          <ModalBody>
            <div className="audience-advanced-fields">
              {[['Backend', 'backend', modelUtils.backendMapping], ['Target', 'target', modelUtils.targetMapping]].map(([label, field, items]) => (
                <label className="audience-model-field" key={field}>{label}
                  <select className="form-control" disabled={!isEditable} value={utils.get(advanced.model, `config.${field}`) ?? 0} onChange={e => changeAdvanced(`config.${field}`, Number(e.target.value))}>
                    {items.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
              ))}
              {[['Input width', 'config.input_size.width'], ['Input height', 'config.input_size.height'], ['Async queue size', 'config.async_queue_size']].map(([label, path]) => (
                <label className="audience-model-field" key={path}>{label}
                  <input className="form-control" type="number" min="1" step="1" placeholder="Model default" disabled={!isEditable}
                    value={utils.get(advanced.model, path) ?? ''} onChange={e => {
                      if (e.target.value === '') {
                        const model = utils.deepClone(advanced.model); utils.remove(model, path); setAdvanced({ ...advanced, model });
                      } else changeAdvanced(path, Number(e.target.value));
                    }} />
                </label>
              ))}
            </div>
            <small>Queue size is used when Async Mode is enabled.</small>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-secondary" type="button" onClick={() => setAdvanced(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={!isEditable}>Apply</button>
          </ModalFooter>
        </form>}
      </Modal>
    </div>
  );
};

export default AudienceModels;
